import { useState, useCallback, useEffect } from 'react';

interface UseNativeCropReturn {
  isOpen: boolean;
  imageSrc: string;
  openCrop: (file: File) => void;
  closeCrop: () => void;
  handleCropComplete: (croppedFile: File) => void;
}

interface UseNativeCropProps {
  onCropComplete: (file: File) => void;
}

export const useNativeCrop = ({ onCropComplete }: UseNativeCropProps): UseNativeCropReturn => {
  const [isOpen, setIsOpen] = useState(false);
  const [imageSrc, setImageSrc] = useState('');

  const openCrop = useCallback((file: File) => {
    // Use Blob URL instead of Data URL to avoid 2-3MB base64 in React state
    // This reduces state size from ~2-3MB to ~50 bytes (blob reference)
    const blobUrl = URL.createObjectURL(file);
    setImageSrc(blobUrl);
    setIsOpen(true);
  }, []);

  const closeCrop = useCallback(() => {
    setIsOpen(false);
    setImageSrc('');
  }, []);

  const handleCropComplete = useCallback((croppedFile: File) => {
    onCropComplete(croppedFile);
    closeCrop();
  }, [onCropComplete, closeCrop]);

  // Cleanup blob URL when modal closes to prevent memory leaks
  useEffect(() => {
    return () => {
      if (imageSrc?.startsWith('blob:')) {
        URL.revokeObjectURL(imageSrc);
      }
    };
  }, [imageSrc]);

  return {
    isOpen,
    imageSrc,
    openCrop,
    closeCrop,
    handleCropComplete,
  };
};
